import clsx from "clsx";
import type { Ref } from "react";
import { forwardRef, memo } from "react";
import { BounceLoader } from "react-spinners";
import { useTranslation } from "react-i18next";

import formClasses from "../styles.module.scss";
import type { IIconRendererProps } from "..";

import classes from "./styles.module.scss";
import type { IInputProps } from "./types";

import { Icon } from "@/components/atoms/icons";
import { Tooltip } from "@/components/atoms/tooltip";
import { IconButton } from "@/components/molecules/iconButton";
import { useReadOnly } from "@/hooks";
import { isDigit, isNumber, p2e } from "@/utils";
import { getDeviceType } from "@/utils/detector";

const ForwardedInput = forwardRef(function Input(
  props: IInputProps<string>,
  ref: Ref<HTMLInputElement>,
) {
  const {
    className,
    onChangeValue,
    inputType = "text",
    dir,
    validation,
    value,
    readOnly: propsReadOnly,
    disabled,
    size,
    icon,
    showIcon,
    onIconClick,
    isError,
    isLoading,
    name,
    onIconMouseDown,
    iconType,
    maxNumber,
    minNumber,
    maxLength,
    isDate,
    ...rest
  } = props;
  const readOnly = useReadOnly(propsReadOnly);
  const hasIcon = (icon && showIcon !== false) || isLoading || isError;
  const deviceType = getDeviceType();

  return (
    <div className={classes.container}>
      <input
        ref={ref}
        className={clsx([
          formClasses["form-control"],
          classes.input,
          className,
        ])}
        onChange={(event) => {
          const targetValue = p2e(event.target.value).trimStart();

          if (isDate) {
            onChangeValue?.(targetValue);
            return;
          }

          if (maxLength && targetValue.trim().length > maxLength) {
            return;
          }

          if (inputType === "digit" && !isDigit(targetValue.trim())) {
            return;
          }

          if (inputType === "customNumber" && !isNumber(targetValue)) {
            return;
          }
          if (
            targetValue !== "" &&
            maxNumber !== undefined &&
            Number(targetValue) > maxNumber
          ) {
            return;
          }
          if (
            targetValue !== "" &&
            minNumber !== undefined &&
            Number(targetValue) < minNumber
          ) {
            return;
          }
          onChangeValue?.(targetValue);
        }}
        type={
          deviceType !== "desktop" &&
          (inputType === "customNumber" || inputType === "digit")
            ? "number"
            : inputType
        }
        value={value || ""}
        readOnly={readOnly}
        disabled={disabled || isLoading}
        data-validation={validation}
        data-size={size}
        onKeyDown={props.onKeyDown}
        data-has-icon={Boolean(hasIcon)}
        name={name}
        dir={
          dir ||
          (inputType === "customNumber" && !!value ? "ltr" : document.body.dir)
        }
        {...rest}
      />
      {hasIcon && (
        <div
          className={clsx([
            classes.iconContainer,
            (iconType !== "button" || isError || isLoading) &&
              classes.imageIcon,
            disabled ? "color-secondary-text" : "color-primary-main",
          ])}
        >
          <IconRenderer
            isLoading={isLoading}
            isError={isError}
            icon={icon}
            iconType={iconType}
            onIconClick={onIconClick}
            onIconMouseDown={onIconMouseDown}
          />
        </div>
      )}
    </div>
  );
});

export const Input = memo(ForwardedInput);

function IconRenderer(props: IIconRendererProps) {
  const { t } = useTranslation("form");
  const {
    icon,
    isError,
    isLoading,
    iconType = "image",
    onIconClick,
    onIconMouseDown,
  } = props;

  if (isLoading) {
    return <BounceLoader size={20} color={"#29A9E14D"} loading />;
  }
  if (isError) {
    return (
      <Tooltip text={t("errorOcurred")}>
        <Icon name={"Danger"} color={"danger-main"} type={"bold"} />
      </Tooltip>
    );
  }
  if (iconType === "button") {
    return (
      <IconButton
        icon={typeof icon === "function" ? icon() : icon}
        label={""}
        noTooltip
        type={"button"}
        className={"color-inherit"}
        transparent
        onClick={onIconClick}
        onMouseDown={onIconMouseDown}
      />
    );
  }
  if (typeof icon === "function") {
    return icon();
  }
  return <>{icon}</>;
}
